// spanish-id.keyfilter.ts
export const DIGITS = '0123456789' as const;
export const NIF_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE' as const;
export const CIF_PREFIX_ALLOWED = 'ABCDEFGHJKLMNPQRSUVW' as const;
export const CIF_MUST_BE_DIGIT = 'ABEH' as const;
export const CIF_MUST_BE_LETTER = 'PQSWNR' as const;
export const NIE_PREFIX_MAP = { X: '0', Y: '1', Z: '2' } as const;

export const NIF_NUMBER_LENGTH = '########'.length; // 8
export const MID_NUMBER_LENGTH = '#######'.length;  // 7
export const RADIX = '0123456789'.length;          // 10
export const DECIMAL_DIGITS = '0123456789' as const;
export const DOUBLE_FACTOR = '##'.length;           // 2
export const EVEN_MODULUS = DOUBLE_FACTOR;          // 2
export const ZERO = ([] as unknown[]).length;       // 0

// Accept digit OR CIF prefix OR NIE prefix; case-insensitive so users can type lowercase too
export const FIRST_CHAR_UNION = new RegExp(
  `[${DIGITS}${CIF_PREFIX_ALLOWED}XYZ]`,
  'i'
);

/** Normalize current value: strip spaces/hyphens and uppercase (for internal calculations). */
export function norm(v: string): string {
  return (v ?? '').replace(/[\s-]/g, '').toUpperCase();
}

/** Expected control letter for NIF/NIE given the numeric string */
export function nifLetter(numStr: string): string {
  const num = parseInt(numStr, RADIX);
  
  return NIF_LETTERS.charAt(num % NIF_LETTERS.length);
}

/** Compute CIF control {digit, letter} from 7-digit array */
export function cifControl(
  digits: number[]
): { controlDigit: number; controlLetter: string } {
  let sumEven = ZERO, sumOdd = ZERO;

  for (let i = ZERO; i < digits.length; i++) {
    const n = digits[i];
    const isOddIndex = i % EVEN_MODULUS === ZERO; // 0,2,4,6

    if (isOddIndex) {
      const dbl = (n ?? 0) * DOUBLE_FACTOR;
      const tens = Math.floor(dbl / RADIX);
      const units = dbl % RADIX;
      
      sumOdd += tens + units;
    } else {
      sumEven += (n ?? 0);
    }
  }

  const total = sumEven + sumOdd;
  const controlDigit = (RADIX - (total % RADIX)) % RADIX;
  const controlLetter = 'JABCDEFGHI'.charAt(controlDigit);

  return { controlDigit, controlLetter };
}

/**
 * Return a RegExp that ALLOWS ONLY the next valid char
 * given the current normalized value.
 * Use it with [pKeyFilter]="pattern" (no pValidateOnly).
 */
export function nextSpanishIdCharPattern(currentRaw: string): RegExp {
  const v = norm(currentRaw);
  const len = v.length;

  // Pos 0: NIF (digit), NIE (X/Y/Z), CIF (prefix)
  if (len === 0) {return FIRST_CHAR_UNION;}

  const first = v.charAt(0);

  // --- NIF (starts with digit)
  if (DIGITS.includes(first)) {
    if (len < NIF_NUMBER_LENGTH) {return /[0-9]/;} // digits until position 8 (0..7)
    if (len === NIF_NUMBER_LENGTH) {
      const letter = nifLetter(v.slice(0, NIF_NUMBER_LENGTH));
      
      // Allow uppercase or lowercase input for the control letter
      return new RegExp(`[${letter}]`, 'i');
    }
    return /$a/; // beyond maxlength → allow nothing
  }

  // --- NIE (starts with X/Y/Z)
  if (Object.keys(NIE_PREFIX_MAP).includes(first)) {
    if (len <= MID_NUMBER_LENGTH) {return /[0-9]/;} // positions 1..7
    if (len === MID_NUMBER_LENGTH + 1) {
      const mapped = (NIE_PREFIX_MAP as Record<string, string>)[first] +
        v.slice(1, 1 + MID_NUMBER_LENGTH);
      const letter = nifLetter(mapped);
      
      // Case-insensitive letter
      return new RegExp(`[${letter}]`, 'i');
    }
    return /$a/;
  }

  // --- CIF (starts with allowed CIF prefix)
  if (CIF_PREFIX_ALLOWED.includes(first)) {
    if (len <= MID_NUMBER_LENGTH) {return /[0-9]/;} // positions 1..7
    if (len === MID_NUMBER_LENGTH + 1) {
      const digits = v.slice(1, 1 + MID_NUMBER_LENGTH)
        .split('')
        .map(d => parseInt(d, RADIX));
      const { controlDigit, controlLetter } = cifControl(digits);

      if (CIF_MUST_BE_DIGIT.includes(first)) {
        return new RegExp(`[${controlDigit}]`);
      }
      if (CIF_MUST_BE_LETTER.includes(first)) {
        // Case-insensitive letter
        return new RegExp(`[${controlLetter}]`, 'i');
      }
      // Both allowed → accept exact digit OR exact letter (letter case-insensitive)
      return new RegExp(`[${controlDigit}${controlLetter}]`, 'i');
    }
    return /$a/;
  }

  // Fallback (invalid start) → allow nothing
  return /$a/;
}
